import * as React from 'react';
import { useContext } from 'react';
import { AuthContext } from "../lib/core";
import { collection, getDocs } from 'firebase/firestore';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTheme } from '@mui/material/styles';
import { Alert, Box, Button, Card, CardActions, CardHeader, Container, CardMedia, CardContent, Grid, Paper, Typography, styled, Accordion, AccordionSummary, AccordionDetails, Checkbox } from "@mui/material";
import IconButton from '@mui/material/IconButton';
import Avatar from '@mui/material/Avatar';
import AvatarGroup from '@mui/material/AvatarGroup';
import Stack from '@mui/material/Stack';
import { blueGrey, green } from '@mui/material/colors';
import "./ProductCard.css";
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import Chip from '@mui/material/Chip';
import Rating from '@mui/material/Rating';

export default function ProductCard(props) {
  const theme = useTheme();
  const [selectedAddons, setSelectedAddons] = React.useState([]);
  const [error, setError] = React.useState("");
  const { firestoreInstance } = useContext(AuthContext);

  // Helper: Check if all selected addons can be provided by one provider using live Firestore query
  async function canAllAddonsBeProvidedByOneProvider(selectedAddonIds, providers) {
    for (const provider of providers) {
      const providerAddonsRef = collection(firestoreInstance, `/userServiceAccounts/${provider.user_id}/addons`);
      try {
        const snapshot = await getDocs(providerAddonsRef);
        const providerAddonIds = snapshot.docs.map(doc => doc.id);
        if (selectedAddonIds.every(id => providerAddonIds.includes(id))) {
          return provider;
        }
      } catch (e) {
        // ignore error, try next provider
      }
    }
    return null;
  }

  const handleAddonSelect = (addonId) => {
    setSelectedAddons(prev =>
      prev.includes(addonId)
        ? prev.filter(id => id !== addonId)
        : [...prev, addonId]
    );
  };

  React.useEffect(() => {
    let isMounted = true;
    async function validateAddons() {
      if (selectedAddons.length > 0) {
        const providers = props.service.providers || [];
        const provider = await canAllAddonsBeProvidedByOneProvider(selectedAddons, providers);
        if (isMounted) {
          if (!provider) {
            setError("All selected add-ons must be provided by the same provider. Please adjust your selection.");
          } else {
            setError("");
          }
        }
      } else {
        if (isMounted) setError("");
      }
    }
    validateAddons();
    return () => { isMounted = false; };
  }, [selectedAddons, props.service.providers]);

  const handleBook = async (e) => {
    e.preventDefault();
    setError("");
    const providers = props.service.providers || [];
    let selectedProvider = null;
    // Fetch each provider's add-ons from Firestore
    async function fetchProviderAddons(provider) {
      const providerAddonsRef = collection(firestoreInstance, `/userServiceAccounts/${provider.user_id}/addons`);
      try {
        const snapshot = await getDocs(providerAddonsRef);
        return snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
      } catch {
        return [];
      }
    }
    // Fetch all providers' add-ons in parallel
    const providersWithAddons = await Promise.all(providers.map(async (p) => ({
      ...p,
      addons: await fetchProviderAddons(p)
    })));
    if (selectedAddons.length === 0) {
      selectedProvider = providersWithAddons[0];
    } else {
      selectedProvider = await canAllAddonsBeProvidedByOneProvider(selectedAddons, providersWithAddons);
    }
    if (selectedProvider) {
      // Avoid circular references: only pass shallow, primitive fields
      const {
        name,
        image,
        price,
        description,
        product_id,
        addons
      } = props.service;

      // Shallow copy of addons with only primitive fields
      const safeAddons = Array.isArray(addons)
        ? addons.map(a => ({
            id: a.id,
            name: a.name,
            price: a.price,
            description: a.description
          }))
        : [];

      // Shallow copy of provider, but include full serviceAccount object
      const safeProvider = selectedProvider
        ? {
            user_id: selectedProvider.user_id,
            address: selectedProvider.address,
            serviceAccount: selectedProvider.serviceAccount && typeof selectedProvider.serviceAccount === 'object'
              ? { ...selectedProvider.serviceAccount }
              : undefined,
            user: selectedProvider.user && typeof selectedProvider.user === 'object'
              ? {
                  displayName: selectedProvider.user.displayName,
                  photoURL: selectedProvider.user.photoURL
                }
              : undefined,
            addons: selectedProvider.addons
          }
        : undefined;

      // Shallow copy of providers array, now with addons and full serviceAccount
      const safeProviders = Array.isArray(providersWithAddons)
        ? providersWithAddons.map(p => ({
            user_id: p.user_id,
            address: p.address,
            serviceAccount: p.serviceAccount && typeof p.serviceAccount === 'object'
              ? { ...p.serviceAccount }
              : undefined,
            user: p.user && typeof p.user === 'object'
              ? {
                  displayName: p.user.displayName,
                  photoURL: p.user.photoURL
                }
              : undefined,
            addons: p.addons
          }))
        : [];

      // Find selected add-ons' full info
      const selectedAddonsFull = safeAddons.filter(a => selectedAddons.includes(a.id));

      props.showDetails({
        name,
        image,
        price,
        description,
        product_id,
        addons: safeAddons,
        provider: safeProvider,
        providers: safeProviders,
        selectedAddons: selectedAddonsFull
      });
    } else {
      setError("All selected add-ons must be provided by the same provider. Please adjust your selection.");
    }
  };

  return (
    <Card className="product-card" elevation={0} sx={{ boxShadow: "none" }}>
      <Box className="product-hero" sx={{ minHeight: 74, display: 'flex', alignItems: 'center', p: 1 }}>
        <Box className="product-title-overlay" sx={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <div className="product-title-thumb">
              <img src={props.service.image || ''} alt={props.service.name} />
            </div>
            <Box>
              <Typography className="product-title" variant="h6">{props.service.name}</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Rating name="read-only" value={4.2} size="small" readOnly precision={0.1} sx={{ color: '#042027' }} />
              </Box>
            </Box>
          </Box>
          <Box>
            <span className="price-badge">${props.service.price}</span>
          </Box>
        </Box>
      </Box>
      <CardContent sx={{pb: 0}}>
        <Box className="product-meta">
          {props.service.providers && (
            <Box className="provider-strip product-avatars">
              <AvatarGroup
                max={4}
                sx={{ '& .MuiAvatar-root': { width: 36, height: 36, fontSize: 12 } }}
              >
                {props.service.providers.map((item, index) => (
                  <Avatar key={index} alt={item.user?.displayName} src={item.user?.photoURL} />
                ))}
              </AvatarGroup>
            </Box>
          )}
          <Typography variant="body2" component="div" align="left" color="text.secondary" className="product-description" sx={{ mt: 1 }}>
            { <div dangerouslySetInnerHTML={{ __html: props.service.description }} /> }
          </Typography>
        </Box>

        {/* Add-ons Accordion */}
        {Array.isArray(props.service.addons) && props.service.addons.length > 0 && (
          <Box sx={{ mt: 2, mb: 2 }}>
            <Accordion>
              <AccordionSummary className="product-addons-header"
                expandIcon={<ExpandMoreIcon />}
                aria-controls="addons-content"
                id="addons-header"
                sx={{ borderRadius: 1 }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Available Add-ons
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box className="product-addons">
                  {props.service.addons.map((addon, idx) => (
                    <Box key={addon.id || idx} className="product-addon-card" sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, flex: 1 }}>
                        <Checkbox size="small" sx={{ mt: '4px' }} checked={selectedAddons.includes(addon.id)} onChange={() => handleAddonSelect(addon.id)} inputProps={{ 'aria-label': `select-addon-${addon.id || idx}` }} />
                        <Box>
                          <Typography variant="body1" sx={{ fontWeight: 700, color: '#1976d2' }}>{addon.name || 'Add-on'}</Typography>
                          {addon.description && addon.description.trim() !== '' && (
                            <Typography variant="body2" color="text.secondary">{addon.description}</Typography>
                          )}
                        </Box>
                      </Box>
                      <Typography variant="body1" sx={{ fontWeight: 700, color: '#1976d2', minWidth: 72, textAlign: 'right' }}>{typeof addon.price !== 'undefined' ? `$${addon.price}` : ''}</Typography>
                    </Box>
                  ))}
                </Box>
              </AccordionDetails>
            </Accordion>
            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>
            )}
          </Box>
        )}
      </CardContent>
      <CardActions sx={{justifyContent: 'center', mb:1}}>
        <Button
          className="product-cta"
          variant="contained"
          color="primary"
          startIcon={<EventAvailableIcon />}
          onClick={handleBook}
          disabled={!!error}
        >
          Book
        </Button>
      </CardActions>
    </Card>
  );
}